import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { ARTICLES } from "@/data/journal";
import { SITE } from "@/data/site";
import Footer from "@/components/Footer";

function Block({ block }) {
  if (block.t === "h2") {
    return (
      <h2 className="mt-16 font-serif text-3xl font-light tracking-tight text-forest lg:text-4xl">
        {block.text}
      </h2>
    );
  }
  if (block.t === "ul") {
    return (
      <ul className="mt-6 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
        {block.items.map((item) => (
          <li key={item} className="flex items-baseline gap-3 border-b border-forest/10 py-3 text-sm leading-relaxed text-forest/75">
            <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-clay" /> {item}
          </li>
        ))}
      </ul>
    );
  }
  if (block.t === "qa") {
    return (
      <div className="mt-8 border-b border-forest/10 pb-8">
        <h3 className="font-serif text-xl font-light text-forest lg:text-2xl">{block.q}</h3>
        <p className="mt-3 text-base leading-relaxed text-forest/70">{block.a}</p>
      </div>
    );
  }
  return <p className="mt-6 text-base leading-relaxed text-forest/75 lg:text-lg">{block.text}</p>;
}

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug) || ARTICLES[0];

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${article.title} | ${SITE.name}, ${SITE.credential} Vancouver`;
    const desc = document.querySelector('meta[name="description"]');
    const prev = desc?.getAttribute("content");
    desc?.setAttribute("content", article.excerpt);
    return () => {
      document.title = "Jordan Martel, RMT | Registered Massage Therapist Vancouver — West End & East Hastings";
      if (prev) desc?.setAttribute("content", prev);
    };
  }, [article]);

  return (
    <div className="bg-bone font-sans text-forest antialiased">
      <header className="border-b border-sand/60 bg-bone/70 backdrop-blur-xl" data-testid="article-header">
        <div className="mx-auto flex max-w-[900px] items-center justify-between px-6 py-5">
          <Link to="/" data-testid="article-back-link" className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-forest/70 transition-colors duration-300 hover:text-forest">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Back to site
          </Link>
          <p className="font-serif text-lg tracking-tight text-forest">
            {SITE.name} <span className="text-clay">{SITE.credential}</span>
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-[900px] px-6 py-16 lg:py-24">
        <article data-testid="article-content">
          <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-clay">
            <span className="h-px w-10 bg-clay" /> Patient journal · {article.date} · {article.readTime}
          </p>
          <h1 className="mt-8 font-serif text-4xl font-light leading-tight tracking-tighter text-forest sm:text-5xl" data-testid="article-title">
            {article.title}
          </h1>
          <p className="mt-6 text-sm uppercase tracking-[0.2em] text-forest/50">By {article.author}</p>

          <div className="mt-12">
            {article.blocks.map((b, i) => (
              <Block key={i} block={b} />
            ))}
          </div>
        </article>

        <div className="mt-20 border border-forest/10 bg-sand/40 p-8 lg:p-12" data-testid="article-cta">
          <h2 className="font-serif text-3xl font-light tracking-tight text-forest">Ready to book?</h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-forest/70">
            Jordan practises at two Vancouver clinics — choose whichever suits
            your day. Real-time availability and instant confirmation online.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            {SITE.locations.map((loc) => (
              <a
                key={loc.id}
                href={loc.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`article-book-${loc.id}`}
                className="group inline-flex items-center gap-3 rounded-full bg-pine px-7 py-3.5 text-xs uppercase tracking-[0.2em] text-bone transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-pine/25"
              >
                Book — {loc.area}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
